import { useCallback, useEffect, useMemo, useState } from "react";
import { ElementItemProps } from "../types";
import { useIntl } from "react-intl";
import { useDevice } from "../../../../utils/DeviceProvider";
import { FormControlLabel, Grid, Radio, RadioGroup, Skeleton, Typography } from "@mui/material";
import Info from "./Info";
import { ComboxElement } from "./Combox";
import { useLocale } from "../../../../locales/LocaleProvider";
import { getLabel } from "../../../../utils/utils";
import { TxtboxElement } from "./Txtbox";
import ElementFrame from "./ElementFrame";
import { useEditedColor } from "../../../../hooks/useEditedStyles";
import { ResetAdornment, RestoreButton, useResetContextMenu } from "../../../../utils/ResetContextMenuProvider";
import { isEqual } from "lodash-es";

export interface OSENV123Parameters {
  index: number;
  version: number;
  typeItemsMask?: number;
}

interface OSENV1Value {
  id: string;
  type: string;
  min?: string;
  max?: string;
  hyst?: string;
  alert?: string;
  zone: string;
}

const OSENV123 = ({ configKey, elementKey, index, version, typeItemsMask, disabled }: Omit<ElementItemProps<OSENV123Parameters>, "label">) => {
  const [active] = useState({ active: true });
  const [configIndex, setConfigIndex] = useState<number | null>(null);
  const [saved, setSaved] = useState<OSENV1Value | null>(null);
  const [id, setId] = useState<string | null>(null);
  const [type, setType] = useState("");
  const [min, setMin] = useState("");
  const [max, setMax] = useState("");
  const [hyst, setHyst] = useState("");
  const [alert, setAlert] = useState("");
  const [zone, setZone] = useState("");

  const { formatMessage } = useIntl();
  const {
    device: { currentValue, config },
    setCurrentValue,
  } = useDevice();
  const { locale } = useLocale();
  const { background: editedBackground } = useEditedColor();
  const { openContextMenu } = useResetContextMenu();

  const typeItems = useMemo(() => {
    return [
      { hu: "Hőmérséklet", en: "Temperature" },
      { hu: "Páratartalom", en: "Humidity" },
      { hu: "Fény", en: "Light" },
      { hu: "Szén-monoxid", en: "Carbon monoxide" },
      { hu: "Víz detektálás", en: "Water detection" },
      { hu: "Nyitás/zárás detektálás", en: "Open/close detection" },
      { hu: "Mozgás detektálás", en: "Motion detection" },
      { hu: "Légmozgás detektálás", en: "Air motion detection" },
      { hu: "Ülés detektálás", en: "Seating detection" },
      { hu: "Világítás detektálás", en: "Light detection" },
      { hu: "Áram mérés", en: "Current meter" },
      { hu: "Gyorsulás mérés", en: "Accelerometer" },
      { hu: "Nyomógomb", en: "Pushbutton" },
    ]
      .filter((_, i) => !typeItemsMask || Math.pow(2, i) & typeItemsMask)
      .map((label, i) => ({ value: `${i}`, label: getLabel(label, locale) }));
  }, [typeItemsMask, locale]);

  const onChange = useCallback(
    ({ id, type, min, max, hyst, alert, zone }: { id?: string; type?: string; min?: string; max?: string; hyst?: string; alert?: string; zone?: string }) => {
      if (configIndex === null) {
        return;
      }
      if (!currentValue[configKey].items[configIndex].value) {
        currentValue[configKey].items[configIndex].value = {};
      }
      if (id !== undefined) {
        currentValue[configKey].items[configIndex].value.id = id;
      }
      if (type !== undefined) {
        currentValue[configKey].items[configIndex].value.type = type;
      }
      if (min !== undefined) {
        currentValue[configKey].items[configIndex].value.min = min;
      }
      if (max !== undefined) {
        currentValue[configKey].items[configIndex].value.max = max;
      }
      if (hyst !== undefined) {
        currentValue[configKey].items[configIndex].value.hyst = hyst;
      }
      if (alert !== undefined) {
        currentValue[configKey].items[configIndex].value.alert = alert;
      }
      if (zone !== undefined) {
        const zoneValue = zone.padStart(3, "0");
        if (zone !== zoneValue) {
          setZone(zoneValue);
        }
        currentValue[configKey].items[configIndex].value.zone = zoneValue;
      }
      setCurrentValue(currentValue);
    },
    [setCurrentValue, currentValue, configKey, configIndex]
  );

  const handleAlertChange = useCallback((_, value: string) => {
    setAlert(value);
  }, []);

  useEffect(() => {
    active.active = true;

    return () => {
      active.active = false;
    };
  }, [active]);

  useEffect(() => {
    if (!currentValue) {
      return;
    }
    const cIndex = currentValue[configKey]?.items?.findIndex(({ type, key }: { type: string; key: string }) => type === `OSENV${version}` && (key === elementKey || `${key}` === elementKey)) ?? null;
    setConfigIndex(cIndex);
  }, [configKey, currentValue, index, version, elementKey]);

  useEffect(() => {
    if (!currentValue || !config || configIndex === null || configIndex === -1) {
      return;
    }
    const svd = config.toolboxConfig[configKey]?.items?.[configIndex].value ?? null;
    setSaved(svd);
    const { id, type, min, max, hyst, alert, zone } = currentValue[configKey].items[configIndex]?.value ?? {};
    setId(id ?? "");
    setType(type ?? "");
    setMin(min ?? "");
    setMax(max ?? "");
    setHyst(hyst ?? "");
    setAlert(alert ?? "");
    setZone(zone ?? "");
  }, [active, currentValue, config, configIndex, configKey]);

  if (id === null) {
    return (
      <Grid container spacing={1}>
        <Grid item>
          <Info text={`${index + 1}.`} inlineTitle />
        </Grid>
        <Grid item>
          <Skeleton variant="rectangular" height={40} sx={{ width: 140, borderRadius: 1 }} />
        </Grid>
        <Grid item>
          <Skeleton variant="rectangular" height={40} sx={{ width: 220, borderRadius: 1 }} />
        </Grid>
        {version !== 2 ? (
          <>
            <Grid item>
              <Skeleton variant="rectangular" height={40} sx={{ width: 120, borderRadius: 1 }} />
            </Grid>
            <Grid item>
              <Skeleton variant="rectangular" height={40} sx={{ width: 120, borderRadius: 1 }} />
            </Grid>
            <Grid item>
              <Skeleton variant="rectangular" height={40} sx={{ width: 120, borderRadius: 1 }} />
            </Grid>
          </>
        ) : null}
        {version > 1 ? (
          <Grid item>
            <Skeleton variant="rectangular" height={40} sx={{ width: 110, borderRadius: 1 }} />
          </Grid>
        ) : null}
        <Grid item>
          <Skeleton variant="rectangular" height={40} sx={{ width: 80, borderRadius: 1 }} />
        </Grid>
      </Grid>
    );
  }

  if (configIndex === -1) {
    return <Typography>{formatMessage({ defaultMessage: "Config not found." })}</Typography>;
  }

  return (
    <Grid container spacing={1} sx={{ alignItems: "flex-end" }}>
      <Grid item>
        <Info text={`${index + 1}.`} inlineTitle />
      </Grid>
      <Grid item>
        <TxtboxElement
          value={id ?? "."}
          label={formatMessage({ defaultMessage: "Id" })}
          onChange={setId}
          onBlur={() => onChange({ id })}
          edited={(saved?.id ?? "") !== id}
          enabledCharacters="0-9"
          disabled={disabled}
          sx={{ width: 140 }}
          onContextMenu={saved && saved.id !== id ? (e) => openContextMenu(e, `${configKey}.items.${configIndex}.value.id`) : undefined}
        />
      </Grid>
      <Grid item>
        <ComboxElement
          value={type ?? ""}
          label={formatMessage({ defaultMessage: "Type" })}
          items={typeItems}
          onChange={setType}
          onBlur={() => onChange({ type })}
          edited={(saved?.type ?? "") !== type}
          disabled={disabled}
          sx={{ width: 220 }}
          onContextMenu={saved && saved.type !== type ? (e) => openContextMenu(e, `${configKey}.items.${configIndex}.value.type`) : undefined}
        />
      </Grid>
      {version !== 2 ? (
        <>
          <Grid item>
            <TxtboxElement
              value={min ?? ""}
              label={formatMessage({ defaultMessage: "Min" })}
              onChange={setMin}
              onBlur={() => onChange({ min })}
              edited={(saved?.min ?? "") !== min}
              number
              inputProps={{ step: 0.1 }}
              disabled={disabled}
              sx={{ width: 120 }}
              onContextMenu={saved && saved.min !== min ? (e) => openContextMenu(e, `${configKey}.items.${configIndex}.value.min`) : undefined}
            />
          </Grid>
          <Grid item>
            <TxtboxElement
              value={max ?? ""}
              label={formatMessage({ defaultMessage: "Max" })}
              onChange={setMax}
              onBlur={() => onChange({ max })}
              edited={(saved?.max ?? "") !== max}
              number
              inputProps={{ step: 0.1 }}
              disabled={disabled}
              sx={{ width: 120 }}
              onContextMenu={saved && saved.max !== max ? (e) => openContextMenu(e, `${configKey}.items.${configIndex}.value.max`) : undefined}
            />
          </Grid>
          <Grid item>
            <TxtboxElement
              value={hyst ?? ""}
              label={formatMessage({ defaultMessage: "Hyst" })}
              onChange={setHyst}
              onBlur={() => onChange({ hyst })}
              edited={(saved?.hyst ?? "") !== hyst}
              number
              inputProps={{ step: 0.1, min: 0 }}
              disabled={disabled}
              sx={{ width: 120 }}
              onContextMenu={saved && saved.hyst !== hyst ? (e) => openContextMenu(e, `${configKey}.items.${configIndex}.value.hyst`) : undefined}
            />
          </Grid>
        </>
      ) : null}
      {version > 1 ? (
        <Grid item>
          <ElementFrame label={formatMessage({ defaultMessage: "Send alert" })} disabled={disabled}>
            <RadioGroup
              sx={{
                flexDirection: "row",
                alignItems: "center",
                paddingLeft: 1,
                borderRadius: 1,
                ...((saved?.alert ?? "") !== alert ? { backgroundColor: editedBackground } : {}),
              }}
              value={alert}
              onChange={handleAlertChange}
              onBlur={() => onChange({ alert })}
              onContextMenu={saved && saved.alert !== alert ? (e) => openContextMenu(e, `${configKey}.items.${configIndex}.value.alert`) : undefined}
            >
              <FormControlLabel value="0" control={<Radio size="small" />} label="0" />
              <FormControlLabel value="1" control={<Radio size="small" />} label="1" />
              {saved && saved?.alert !== alert ? <ResetAdornment sx={{ mr: 1 }} /> : null}
            </RadioGroup>
          </ElementFrame>
        </Grid>
      ) : null}
      <Grid item>
        <TxtboxElement
          value={zone}
          label={formatMessage({ defaultMessage: "Zone" })}
          onChange={setZone}
          onBlur={() => onChange({ zone })}
          edited={(saved?.zone ?? "") !== zone}
          hex
          maxLength={3}
          disabled={disabled}
          sx={{ width: 80 }}
          onContextMenu={saved && saved.zone !== zone ? (e) => openContextMenu(e, `${configKey}.items.${configIndex}.value.zone`) : undefined}
        />
      </Grid>
      {(
        !saved
          ? id || type || min || max || hyst || alert || zone
          : !isEqual(
              saved,
              Object.fromEntries(
                [
                  ["id", id],
                  ["type", type],
                  version !== 2 ? ["min", min] : null,
                  version !== 2 ? ["max", max] : null,
                  version !== 2 ? ["hyst", hyst] : null,
                  version > 1 ? ["alert", alert] : null,
                  ["zone", zone],
                ].filter((i) => !!i) as [string, string][]
              )
            )
      ) ? (
        <Grid item>
          <RestoreButton path={`${configKey}.items.${configIndex}.value`} saved={saved} />
        </Grid>
      ) : null}
    </Grid>
  );
};

export default OSENV123;
